<template>
  <v-layout column>
    <v-layout
      column
      align-center
    >
      <div v-show="couldDetermineVideoResolution">
        <div class="body-2 black--text mb-1">
          {{ $t('videoFormatOriginal') }}
        </div>
        <video
          id="originalVideo"
          ref="originalVideo"
          autoplay
          muted
          loop
          controls
          class="outline--black"
          :src="videoUrl"
        />
      </div>
      <div class="mt-3">
        <span v-if="mandatory">
          <span v-if="couldDetermineVideoResolution">
            {{ $t('videoFormatNeedsConversion_1') }}
            <b>{{ originalVideoWidth + ' x ' + originalVideoHeight }}</b>
            {{ $t('videoFormatNeedsConversion_2') }}
          </span>
          <span v-else>
            {{ $t('videoFormatNeedsConversion_couldNotDetermineResolution') }}
          </span>
        </span>
        <span v-else>{{ $t('videoFormatNeedsConversion_0') }}</span>
        {{ $t('videoFormatNeedsConversion_3') }}
      </div>
      <v-layout
        v-if="atLeastOneFormatToChoose"
        column
        align-center
        class="mb-4"
      >
        <v-layout
          v-for="tvformat in sortedUserTVFormatsChoosable"
          :key="tvformat.id"
        >
          <v-checkbox
            v-model="tvformatIdToSelected[tvformat.id]"
            color="msr"
            hide-details
            data-cy="tvformat-checkbox"
          >
            <template slot="label">
              <v-layout
                style="width: 110px"
                class="ml-2"
                justify-center
              >
                <tv-format-preview-card
                  :name="tvformat.name"
                  :original-width="originalVideoWidth"
                  :original-height="originalVideoHeight"
                  :target-width="tvformat.width"
                  :target-height="tvformat.height"
                  :selected="tvformatIdToSelected[tvformat.id]"
                  data-cy="tvformat-preview-card"
                  @click.native.stop="onTVFormatSelected(tvformat)"
                />
              </v-layout>
              <v-subheader style="width: 130px">
                <strong
                  :class="{'msr--text': tvformatIdToSelected[tvformat.id]}"
                >{{ tvformat.width }} x {{ tvformat.height }} px</strong>
              </v-subheader>
            </template>
          </v-checkbox>
        </v-layout>
      </v-layout>
    </v-layout>
  </v-layout>
</template>

<script>
import orderBy from 'lodash.orderby';

import TvFormatPreviewCard from '@/components/TvFormatPreviewCard';

export default {
  components: {
    TvFormatPreviewCard,
  },
  props: {
    /**
     * Whether choosing a format is a required action or optional.
     *
     * This affects how options are presented and the exact wording.
     */
    mandatory: {
      type: Boolean,
      required: true,
    },
    originalVideoFile: {
      type: File,
      required: true,
    },
    chosenFormats: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      originalVideoWidth: 0,
      originalVideoHeight: 0,
      tvformatIdToSelected: {},
    };
  },
  computed: {
    couldDetermineVideoResolution() {
      return this.originalVideoWidth && this.originalVideoHeight;
    },
    syncedChosenFormats: {
      get() {
        return this.chosenFormats;
      },
      set(tvformats) {
        this.$emit('update:chosenFormats', tvformats);
      },
    },
    atLeastOneFormatToChoose() {
      return Object.keys(this.tvformatIdToSelected).length > 0;
    },
    atLeastOneFormatSelected() {
      return this.selectedTVFormats.length > 0;
    },
    shouldRememberPriorSelection() {
      return this.syncedChosenFormats.length > 0;
    },
    userTVFormatsChoosable() {
      return this.$store.state.common.userTVFormatsChoosable;
    },
    sortedUserTVFormatsChoosable() {
      return orderBy(this.userTVFormatsChoosable, 'name');
    },
    selectedTVFormatIds() {
      return Object.keys(this.tvformatIdToSelected).filter((tvformatId) => {
        const selected = this.tvformatIdToSelected[tvformatId];
        return !!selected;
      });
    },
    selectedTVFormats() {
      return this.sortedUserTVFormatsChoosable.filter((tvformat) => {
        const id = String(tvformat.id);
        const selected = this.selectedTVFormatIds.indexOf(id) !== -1;
        return selected;
      });
    },
    videoUrl() {
      if (!this.originalVideoFile) {
        return null;
      }
      return URL.createObjectURL(this.originalVideoFile);
    },
  },
  watch: {
    /** Propagate selection changes to parent component. */
    selectedTVFormats: function () {
      this.syncedChosenFormats = this.selectedTVFormats;
    },
  },
  mounted() {
    this.populateSelectionDict();
    this.loadVideoMetadata();
  },
  methods: {
    onTVFormatSelected(tvformat) {
      const oldValue = this.tvformatIdToSelected[tvformat.id];
      const newValue = !oldValue;
      this.$set(this.tvformatIdToSelected, tvformat.id, newValue);
    },
    preselectMatchingTVFormat() {
      this.sortedUserTVFormatsChoosable.forEach((tvformat) => {
        const hasMatchingDimensions =
          tvformat.width == this.originalVideoWidth &&
          tvformat.height == this.originalVideoHeight;

        const hasMatchingRatio =
          tvformat.width / tvformat.height ==
          this.originalVideoWidth / this.originalVideoHeight;

        if (hasMatchingDimensions || hasMatchingRatio) {
          this.$set(this.tvformatIdToSelected, tvformat.id, true);
        }
      });
    },
    /**
     * Add a key:value pair for every format.
     *
     * Formats are deselected by default, unless remember from an
     * earlier selection.
     */
    populateSelectionDict() {
      const syncedChosenFormatIds = this.syncedChosenFormats.map(
        (tvformat) => tvformat.id
      );

      this.$set(this, 'tvformatIdToSelected', {});
      this.userTVFormatsChoosable.forEach((tvformat) => {
        const selected =
          this.shouldRememberPriorSelection &&
          syncedChosenFormatIds.indexOf(tvformat.id) !== -1;
        this.$set(this.tvformatIdToSelected, tvformat.id, selected);
      });
    },
    loadVideoMetadata() {
      const vm = this;
      const video = vm.$refs.originalVideo;

      function onLoadedMetadata(event) {
        // Store dimensions to use in the label.
        vm.originalVideoWidth = video.videoWidth;
        vm.originalVideoHeight = video.videoHeight;

        // Preselect TVFormat if we have one that matches the video
        // dimensions exactly.
        if (!vm.shouldRememberPriorSelection && vm.mandatory) {
          vm.preselectMatchingTVFormat();
        }
      }

      video.addEventListener('loadedmetadata', onLoadedMetadata, false);
    },
  },
};
</script>

<style scoped>
.outline--black {
  outline: 1px solid black;
}

#originalVideo {
  max-width: 200px;
  max-height: 200px;
}
</style>



// WEBPACK FOOTER //
// src/components/VideoFormatChooser.vue